import { UserDto } from "@/common/api/datasources/Api";
import { useUserVerificationQuery } from "@/common/api/services/userVerification";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

export type ProfileFormValues = z.infer<
  ReturnType<typeof createProfileFormSchema>
>;

interface ProfileFormProps {
  user: UserDto;
  isLoading: boolean;
  onSubmit: (values: ProfileFormValues) => Promise<void>;
}

const createProfileFormSchema = (t: (key: string, options?: any) => string) =>
  z.object({
    firstName: z
      .string()
      .transform((value) => (value === "" ? null : value))
      .refine((value) => value === null || value.length >= 1, {
        message: t("validation.minLength", {
          field: t("settings.profile.form.firstName"),
          length: 1,
        }),
      })
      .refine((value) => value === null || value.length <= 100, {
        message: t("validation.maxLength", {
          field: t("settings.profile.form.firstName"),
          length: 100,
        }),
      })
      .nullable()
      .optional(),
    lastName: z
      .string()
      .transform((value) => (value === "" ? null : value))
      .refine((value) => value === null || value.length >= 1, {
        message: t("validation.minLength", {
          field: t("settings.profile.form.lastName"),
          length: 1,
        }),
      })
      .refine((value) => value === null || value.length <= 100, {
        message: t("validation.maxLength", {
          field: t("settings.profile.form.lastName"),
          length: 100,
        }),
      })
      .nullable()
      .optional(),
    email: z
      .string()
      .min(
        1,
        t("validation.isRequired", { field: t("settings.profile.form.email") }),
      )
      .email(t("validation.email")),
    phoneNumber: z
      .string()
      .min(
        10,
        t("validation.minLength", {
          field: t("settings.profile.form.phoneNumber"),
          length: 10,
        }),
      )
      .max(
        50,
        t("validation.maxLength", {
          field: t("settings.profile.form.phoneNumber"),
          length: 50,
        }),
      )
      .or(z.literal(""))
      .optional(),
    birthDate: z.date().optional().nullable(),
  });

export const ProfileForm = ({
  user,
  isLoading,
  onSubmit,
}: ProfileFormProps) => {
  const { t } = useTranslation();
  const profileFormSchema = createProfileFormSchema(t);
  const { verificationQuery } = useUserVerificationQuery();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? null,
      email: user.email ?? "",
      phoneNumber: user.phoneNumber ?? "",
      birthDate: user.birthDate ? new Date(user.birthDate) : undefined,
    },
  });

  const handleSubmit = async (values: ProfileFormValues) => {
    const formattedValues: ProfileFormValues = {
      ...values,
      birthDate: values.birthDate || null,
    };

    await onSubmit(formattedValues);

    const isEmailChanged = values.email !== user.email;
    const isPhoneChanged = values.phoneNumber !== user.phoneNumber;

    if (isEmailChanged || isPhoneChanged) {
      verificationQuery.refetch();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.title")}</CardTitle>
        <CardDescription>{t("profile.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-8"
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.profile.form.firstName")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("settings.profile.form.firstName")}
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("settings.profile.form.lastName")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("settings.profile.form.lastName")}
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.profile.form.email")}</FormLabel>
                  <FormControl>
                    <Input placeholder="email@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("settings.profile.form.phoneNumber")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="+12025550123"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="birthDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("settings.profile.form.birthDate")}</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[240px] pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground",
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP", { locale: enUS })
                          ) : (
                            <span>{t("settings.profile.form.selectDate")}</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value || undefined}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button type="submit" disabled={isLoading}>
                {isLoading
                  ? t("common.saving")
                  : t("settings.profile.form.submit")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
